import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { post, put, deleteHttp, sendWithFile } from "../../../../common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { useAllSelectableUsersQuery, useSelectableDocumentTypesQueryByUsageType } from "../../../../common/hooks/useSelectableItemQueries";
import { useValidator } from "../../../../common/hooks/validation";
import { useDocumentHook } from "../../../../documents/Documents.hook";
import { IAddEditContractDocumentDialogProps } from "./AddEditContractDocumentDialog";
import { useTranslation } from "../../../../localization/useTranslation";
import { Result } from "../../../../common/api/result";
import { ContractDocumentRow, ContractDocumentUpdateModel } from "../contractDocuments/ContractDocuments.types";
import { isNullOrWhitespace } from "../../../../common/stringOperations";
import { DocumentTypeUsageType } from "../../../../masterData/documentType/documentTypes/DocumentTypes.types";
import { TagRelationCreateModel, TagRelationType } from "../../../../common/components/tagsMultiSelect/TagsMultiSelect.types";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../../../auth/planProvider/PlanProvider";
import { useDotLegalMutation, useDotLegalQueryArray, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { ContractModel } from "../../Contract.types";
import { getContractQueryKey } from "../../Contract.hooks";
import { useGroupEntitiesQuery } from "../../../../processingActivity/hooks/useGroupEntitiesQuery";

export function useAddEditContractDialogDocumentHook(props: IAddEditContractDocumentDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { id } = useParams<{ id: string }>();
    const [document, setDocument] = useState<ContractDocumentUpdateModel>(getSaveModel(props.selectedDocument));
    // The restriction has no persisted flag, so the toggle lives here: an empty selection while the toggle is
    // off means "restricted, nothing picked yet", which validation rejects rather than silently saving as open.
    const [appliesToAllGroupEntities, setAppliesToAllGroupEntities] = useState((props.selectedDocument?.groupEntityAccessIds?.length ?? 0) === 0);
    const [documentationOption, setDocumentationOption] = useState(1);
    const [documentVersionIsUploading, setDocumentVersionIsUploading] = useState(false);
    const [file, setFile] = useState<File>();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();
    const [initialTags, setInitialTags] = useState<Array<string>>([]);
    const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
    const { data, isLoading } = useAllSelectableUsersQuery();
    const { data: documentTypes, isLoading: documentTypesIsLoading } = useSelectableDocumentTypesQueryByUsageType(DocumentTypeUsageType.Contracts);

    const { data: contract, isLoading: contractIsLoading } = useDotLegalQueryWithKey<ContractModel>(
        getContractQueryKey(props.contractId),
        `/contracts/${props.contractId}`
    );

    const showGroupEntityAccessPicker = contract?.hasAccessToMultipleGroupEntities ?? false;

    const { allGroupEntities, isLoading: groupEntitiesIsLoading } = useGroupEntitiesQuery(showGroupEntityAccessPicker);

    const selectableGroupEntities = contract?.allGroupEntityAccess
        ? allGroupEntities
        : allGroupEntities?.filter((groupEntity) => contract?.groupEntities.includes(groupEntity.id));

    const enableTagsQuery =
        props.isEdit && !isNullOrWhitespace(props.selectedDocument?.contractDocumentId) && permissions.canAccessTags && platformFeatures.tags;

    const { data: existingTags } = useDotLegalQueryArray<Array<string>>(
        [`tagRelation/${props.selectedDocument?.contractDocumentId}`, props.selectedDocument?.contractDocumentId],
        `/tagRelation/${props.selectedDocument?.contractDocumentId}`,
        { enabled: enableTagsQuery }
    );

    useEffect(() => {
        if (existingTags && props.isEdit) {
            setInitialTags(existingTags);
            setSelectedTags(existingTags);
        } else if (props.selectedDocument?.tags && props.isEdit) {
            const tagIds = props.selectedDocument.tags.map((tag) => tag.id);
            setInitialTags(tagIds);
            setSelectedTags(tagIds);
        }
    }, [existingTags, props.isEdit, props.selectedDocument?.tags]);

    const validation = useValidator<ContractDocumentUpdateModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            fileName: props.isEdit || documentationOption === 0 ? undefined : validators.validateNotEmpty((item) => item.fileName, translateString("file")),
            link:
                props.isEdit || (!document?.isLink && documentationOption === 1)
                    ? undefined
                    : validators.validateUrlAndNotEmpty((item) => item.link, translateString("link")),
            documentTypeId: validators.validateNotEmpty((item) => item.documentTypeId, translateString("type")),
            status: validators.validateNotEmpty((item) => item.status, translateString("status")),
            groupEntityAccessIds: validators.validateNotEmpty(
                (item) => item.groupEntityAccessIds,
                "",
                () => appliesToAllGroupEntities || !showGroupEntityAccessPicker,
                translateString("oneGroupCompanyIsRequired")
            ),
        }),
        [documentationOption, appliesToAllGroupEntities, showGroupEntityAccessPicker]
    );

    let documentStatus = useDocumentHook().selecableDocumentStatus;

    const addTagRelationMutation = useDotLegalMutation(addTagRelationApi);
    const removeTagRelationMutation = useDotLegalMutation(removeTagRelationApi);

    const createMutation = useDotLegalMutation(createMutationApi);
    const createDocument = () => {
        createMutation.mutateAsync(document!, {
            onSuccess: async (response: Result<string>) => {
                const documentId = response.value();
                if (documentId && selectedTags.length > 0 && permissions.canAccessTags) {
                    await manageTags(documentId, [], selectedTags);
                }
                snackbar.show(translateString("documentCreated"));
                props.onDialogClose();
            },
        });
    };

    const editMutation = useDotLegalMutation(updateMutationApi);
    const updateDocument = () => {
        editMutation.mutateAsync(document!, {
            onSuccess: async (response: Result<string>) => {
                if (props.selectedDocument?.contractDocumentId && permissions.canAccessTags) {
                    await manageTags(props.selectedDocument.contractDocumentId, initialTags, selectedTags);
                }
                snackbar.show(translateString("documentWasUpdated"));
                props.onDialogClose();
            },
        });
    };

    function onFileUpload(file: DotLegalFile[] | undefined) {
        const tempDocument = { ...document! };
        setFile(file?.[0].file);
        tempDocument.fileName = file ? file[0].fileName : "";

        if (isNullOrWhitespace(tempDocument.name)) {
            tempDocument.name = tempDocument.fileName.substring(0, tempDocument.fileName.lastIndexOf("."));
        }

        setDocument(tempDocument);
    }

    const documentDataUpdater = {
        onNameChange: (name: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.name = name ?? undefined;
            });
        },
        onTypeChange: (documentTypeId: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.documentTypeId = documentTypeId ?? undefined;
            });
        },
        onStatusChange: (status: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.status = Number(status);
            });
        },
        onOwnerChange: (owner: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.ownerId = owner ?? undefined;
            });
        },
        onCommentChange: (comment: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.comment = comment ?? undefined;
            });
        },
        onFileUploadChange: onFileUpload,
        onLinkChange: (link: string | null) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.link = link ?? undefined;
            });
        },
        onDocumentationTypeChange: (option: number) => {
            onChange((model: ContractDocumentUpdateModel) => {
                if (option === 0) {
                    setFile(undefined);
                    model.fileName = "";
                    setDocumentationOption(0);
                }

                if (option === 1) {
                    model.link = undefined;
                    setDocumentationOption(1);
                }
            });
        },
        onTagsChange: (tags: Array<string>) => {
            setSelectedTags(tags);
        },
        onGroupEntityAccessChange: (groupEntityAccessIds: Array<string>) => {
            onChange((model: ContractDocumentUpdateModel) => {
                model.groupEntityAccessIds = groupEntityAccessIds;
            });
        },
        onAppliesToAllGroupEntitiesChange: (appliesToAll: boolean) => {
            setAppliesToAllGroupEntities(appliesToAll);

            if (appliesToAll) {
                onChange((model: ContractDocumentUpdateModel) => {
                    model.groupEntityAccessIds = [];
                });
            }
        },
    };

    function onChange(changeFunc: (contract: ContractDocumentUpdateModel) => void) {
        const tempContact = { ...document! };
        changeFunc(tempContact);

        setDocument(tempContact);
    }

    return {
        document,
        setDocument,
        documentTypes,
        inProgress: createMutation.isLoading || editMutation.isLoading,
        isUploading: createMutation.isLoading && file !== undefined,
        uploadPercentage,
        validation,
        validateDocumentForm,
        userOptions: data,
        userOptionsIsLoading: isLoading,
        documentationOption,
        documentStatus,
        documentTypesIsLoading,
        documentDataUpdater,
        documentVersionIsUploading,
        setDocumentVersionIsUploading,
        selectedTags,
        showGroupEntityAccessPicker,
        selectableGroupEntities,
        groupEntityAccessPickerIsLoading: contractIsLoading || groupEntitiesIsLoading,
        appliesToAllGroupEntities,
    };

    async function manageTags(documentId: string, oldTags: Array<string>, newTags: Array<string>) {
        const tagsToAdd = newTags.filter((tag) => !oldTags.includes(tag));
        const tagsToRemove = oldTags.filter((tag) => !newTags.includes(tag));

        for (const tagId of tagsToAdd) {
            const tagRelation: TagRelationCreateModel = {
                tagId: tagId,
                entityId: documentId,
                entityType: TagRelationType.ContractDocument,
            };
            await addTagRelationMutation.mutateAsync(tagRelation);
        }

        for (const tagId of tagsToRemove) {
            await removeTagRelationMutation.mutateAsync({ entityId: documentId, tagId: tagId });
        }
    }

    function createMutationApi(doc: ContractDocumentUpdateModel) {
        return sendWithFile<string>("POST", `/contract/${id}/documents`, doc, file, trackUploadProgress());
    }

    function updateMutationApi(doc: ContractDocumentUpdateModel) {
        return put<string>(`/contract/${doc.documentId}/documents`, doc);
    }

    async function addTagRelationApi(tagRelation: TagRelationCreateModel) {
        return await post("/tagRelation", tagRelation);
    }

    async function removeTagRelationApi(data: { entityId: string; tagId: string }) {
        return await deleteHttp(`/tagRelation/${data.entityId}?tagId=${data.tagId}`);
    }

    function validateDocumentForm() {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (props.isEdit) {
            updateDocument();
        } else {
            createDocument();
        }
    }

    function getSaveModel(selectedDocument?: ContractDocumentRow) {
        const updateModel: ContractDocumentUpdateModel = {
            name: selectedDocument?.name ?? "",
            documentId: selectedDocument?.documentId ?? "",
            documentTypeId: selectedDocument?.documentType.id ?? "",
            ownerId: selectedDocument?.owner.id ?? undefined,
            status: selectedDocument?.status ?? undefined,
            fileName: "",
            isLink: false,
            link: "",
            comment: selectedDocument?.comment ?? "",
            groupEntityAccessIds: selectedDocument?.groupEntityAccessIds ?? [],
        };

        return updateModel;
    }
}
